import React, { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import Image from '@components/Image';
import Modal from '@components/Modals';
import Action from '@components/Button/Action';
import Header from '@components/Modals/Header';
import Item from '@components/CheckMarkItem';
import styles from './Modal.module.scss';
import { TrackingFn } from '@components/Integrations/utils';
import LinkedInCSVModal from '@components/Integrations/Modal/LinkedInCSVModal';
import { useSelector } from 'react-redux';
import {
  selectIntegrationByPlatform,
  selectIntegrationStatusOnDevice,
} from '@reducers/user/integrations';
import { syncDeviceIntegration } from '@actions/deviceIntegrations';

const linkedin = '/img/settings/linkedin-dark.png';
const linkedinLight = '/img/settings/linkedin-light.png';

type ModalRenderLocation = 'settings' | 'onboarding';

interface DetailsProps {
  onSync: () => void;
  renderLocation: ModalRenderLocation;
  onDoCSVImport: () => void;
}

function Details({ onSync, renderLocation, onDoCSVImport }: DetailsProps) {
  let topCopy = 'We will seamlessly sync your LinkedIn connections to Clay in one easy step.';
  let btnCopy = 'Sync connections';
  let actionCopy = 'syncing';

  if (renderLocation == 'onboarding') {
    topCopy =
      'We will seamlessly sync your LinkedIn connections to Clay when you have the Clay app open on your device.';
    btnCopy = 'Connect LinkedIn';
    actionCopy = 'connecting LinkedIn to Clay';
  }

  const showCSVButton = renderLocation === 'settings';
  const forceDarkMode = renderLocation === 'onboarding';

  return (
    <div className={styles.content}>
      <p className="text-secondary">{topCopy}</p>

      <Item title="Secure & Private">
        All your connections from LinkedIn will be imported locally. Your connections information is
        encrypted end-to-end. Clay never has access to your Linkedin username or password.
        Everything happens on device.
      </Item>
      <Item title="Limited Access">
        We will never add a connection, send messages, or take any actions on your behalf.
      </Item>

      <div className={styles.actions}>
        <Action onClick={onSync} block forceDarkMode={forceDarkMode}>
          {btnCopy}
        </Action>
        {showCSVButton && (
          <div>
            <Action onClick={onDoCSVImport} block forceDarkMode={forceDarkMode}>
              Upload CSV
            </Action>
          </div>
        )}
        <div>
          By {actionCopy} you agree to our{' '}
          <a className="link-inherit" href="#">
            privacy policy
          </a>
          .
        </div>
      </div>
    </div>
  );
}

function DetailsWeb({ onDoCSVImport, forceDarkMode = false }) {
  return (
    <div className={styles.content}>
      <p>
        To connect your LinkedIn to Clay, please use the Mac, Windows, or iOS apps.{' '}
        <a href="https://clay.earth/start" target="_blank">
          Get started with our other apps.
        </a>
      </p>

      <p>
        You can also do an import of your LinkedIn connections by uploading a CSV file. This process
        is much quicker when performing the initial sync, especially when you have a large number of
        connections.{' '}
        <a href="https://go.clay.earth/linkedin" target="_blank">
          Learn more here.
        </a>
      </p>

      <div className={styles.actions}>
        <Action onClick={onDoCSVImport} block forceDarkMode={forceDarkMode}>
          Upload CSV
        </Action>
      </div>
    </div>
  );
}

function renderTitle(status, _progress, total) {
  if (status === 'waiting') {
    return 'Waiting for LinkedIn';
  }

  if (status === 'syncing') {
    return <>Syncing Connections&hellip;</>;
  }

  return `Successfully imported ${total} connections`;
}

interface LinkedinModalProps {
  // open: boolean;
  onClose: () => void;
  renderLocation: ModalRenderLocation;
  trackConnection: TrackingFn;
  autoSync?: boolean;
}

export default function LinkedinModal({
  onClose,
  renderLocation,
  trackConnection,
  autoSync,
}: LinkedinModalProps) {
  const integration = useSelector(selectIntegrationByPlatform('linkedin'));
  const integrationStateOnDevice = useSelector(selectIntegrationStatusOnDevice('linkedin'));
  const [status, setStatus] = useState(integrationStateOnDevice?.syncing ? 'syncing' : null);
  const [showCSVModal, setShowCSVModal] = useState(false);

  const onSync = useCallback(() => {
    setStatus('waiting');
    trackConnection({ type: 'Linkedin' });

    if (integrationStateOnDevice?.supported) {
      syncDeviceIntegration('linkedin');
    }
  }, []);

  useEffect(() => {
    if (integrationStateOnDevice?.syncing && status !== 'syncing') {
      setStatus('syncing');
    } else if (!integrationStateOnDevice?.syncing && status === 'syncing') {
      setStatus('complete');
    }
  }, [integrationStateOnDevice?.syncing, status]);

  useEffect(() => {
    if (renderLocation == 'onboarding' && status !== null) {
      onClose();
    }
  }, [status]);

  useEffect(() => {
    if (autoSync) {
      onSync();
    }
  }, []);

  const detailView = status !== null ? 'status' : window.app.isElectron ? 'details' : 'detailsWeb';
  const forceDarkMode = renderLocation === 'onboarding';

  return (
    <Modal
      className={cn(styles.modalWrapper, 'modal-large')}
      forceDarkMode={forceDarkMode}
      onRequestClose={onClose}
      isOpen
      centered
    >
      <Header.Plain onClose={onClose} />
      <div className={styles.content}>
        <Image
          className={styles.image}
          width={207}
          dark={linkedin}
          light={!forceDarkMode && linkedinLight}
        />

        {detailView === 'details' && (
          <Details
            onSync={onSync}
            renderLocation={renderLocation}
            onDoCSVImport={() => setShowCSVModal(true)}
          />
        )}

        {detailView === 'detailsWeb' && (
          <DetailsWeb onDoCSVImport={() => setShowCSVModal(true)} forceDarkMode={forceDarkMode} />
        )}

        {detailView === 'status' && (
          <>
            <h3 className="text-default">
              {renderTitle(
                status,
                integrationStateOnDevice?.progress || 0,
                integration?.status?.total,
              )}
            </h3>
            {status !== 'complete' && (
              <p>
                Please keep the Clay app open until the import is complete. You may close this
                modal.
              </p>
            )}
            <p>
              {status === 'complete' && (
                <>
                  The number of connections ultimately imported may be slightly less than{' '}
                  {integration?.status?.total} due to deleted or duplicate profiles.{' '}
                </>
              )}
              <a className="link-inherit" href="https://go.clay.earth/linkedin" target="_blank">
                Learn more
              </a>{' '}
              about our Linkedin integration.
            </p>

            <div className={styles.actions}>
              <Action onClick={onClose} block forceDarkMode={forceDarkMode}>
                Done
              </Action>
            </div>
          </>
        )}
      </div>

      {showCSVModal && (
        <LinkedInCSVModal onClose={() => setShowCSVModal(false)} forceDarkMode={forceDarkMode} />
      )}
    </Modal>
  );
}
